/**
 * Media Service - управление медиа-библиотекой проекта
 * Firestore: projects/{projectId}/media/{mediaId}
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { uploadImage, deleteImage } from './storage';

/**
 * Получает список медиафайлов проекта
 * @param {string} projectId - ID проекта
 * @returns {Promise<Array>} - список медиафайлов
 */
export async function getProjectMedia(projectId) {
  if (!projectId) {
    throw new Error('Project ID не указан');
  }

  try {
    const mediaRef = collection(db, 'projects', projectId, 'media');
    const snapshot = await getDocs(mediaRef);

    // Сортируем на клиенте (чтобы избежать требования индекса)
    const items = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Сортировка по дате загрузки (новые первые)
    return items.sort((a, b) => {
      const dateA = a.uploadedAt?.toDate?.() || new Date(0);
      const dateB = b.uploadedAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting media:', error);
    throw new Error('Не удалось загрузить медиа-библиотеку');
  }
}

/**
 * Загружает новый медиафайл
 * @param {File} file - файл для загрузки
 * @param {string} projectId - ID проекта
 * @param {Function} onProgress - callback прогресса
 * @returns {Promise<Object>} - метаданные загруженного файла
 */
export async function uploadMedia(file, projectId, onProgress) {
  if (!projectId) {
    throw new Error('Project ID не указан');
  }

  try {
    // 1. Загружаем файл в Storage
    const storageResult = await uploadImage(file, projectId, onProgress);

    // 2. Сохраняем метаданные в Firestore
    const mediaRef = collection(db, 'projects', projectId, 'media');
    const mediaData = {
      src: storageResult.url,
      storagePath: storageResult.path,
      filename: storageResult.filename,
      originalFilename: file.name,
      size: storageResult.size,
      mimeType: storageResult.mimeType,
      uploadedAt: serverTimestamp(),
    };

    const docRef = await addDoc(mediaRef, mediaData);

    return {
      id: docRef.id,
      ...mediaData,
      uploadedAt: new Date(),
    };
  } catch (error) {
    console.error('Error uploading media:', error);
    throw error;
  }
}

/**
 * Удаляет медиафайл
 * @param {string} projectId - ID проекта
 * @param {string} mediaId - ID медиафайла
 * @param {string} storagePath - путь в Storage
 */
export async function deleteMedia(projectId, mediaId, storagePath) {
  if (!projectId || !mediaId) {
    throw new Error('Project ID и Media ID обязательны');
  }

  try {
    // 1. Удаляем из Storage
    if (storagePath) {
      await deleteImage(storagePath);
    }

    // 2. Удаляем документ из Firestore
    const mediaDocRef = doc(db, 'projects', projectId, 'media', mediaId);
    await deleteDoc(mediaDocRef);
  } catch (error) {
    console.error('Error deleting media:', error);
    throw new Error('Не удалось удалить медиафайл');
  }
}

/**
 * Форматирует размер файла в читаемый вид
 * @param {number} bytes - размер в байтах
 * @returns {string} - форматированный размер
 */
export function formatFileSize(bytes) {
  if (!bytes) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
}
